import { ExperienceEditor } from '@/components/admin/experiences/experience-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchExperience, fetchExperienceVersion } from '@/lib/api';
import { useConsumedCopyFlag } from '@/lib/copy-search';
import { readExperienceEditorSearch } from '@/lib/experience-search';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'une expérience existante (refonte modulaire §4, §9.1; maquettes
 * `Timeline`, `OngletPersona`, `OngletHistorique`).
 *
 * ## Two queries, two shapes, two key prefixes (issue #16)
 *
 * `['experience', email, id]` holds an `ExperienceDetail`;
 * `['experience-version', email, id, versionId]` holds an `ExperienceVersion`.
 * Different prefixes on purpose: the trap of #16 is two readers agreeing on a
 * key and disagreeing on what is under it, and `useQuery` is generic per
 * component so nothing would catch the drift. Neither key overlaps
 * `['experiences', email]`, which the list fills with the card projection.
 *
 * ## « Voir », the tab AND the page travel in the URL
 *
 * `?version=` is the read-only opening of §4. `?tab=` and `?page=` are §9.1's,
 * and the reason is the same one every filtered screen here holds: a reload, a
 * back button or a link pasted into a team thread must land on the panel and
 * the page somebody was reading. `readExperienceEditorSearch` never throws, and
 * the versioned layer resolves a `?tab=` naming no tab onto the first one
 * rather than onto an empty panel.
 *
 * The breadcrumb of §9.1 is NOT rendered here, unlike the protocol's: under the
 * tabs layout it sits above the head, inside the editor, which is where the
 * maquette puts it and where `VersionedEditor` takes it as a node.
 *
 * ## `key` on the editor, and what it must NOT contain
 *
 * The editor initialises its draft state from `detail` ONCE, with no effect
 * copying a query into state. So a reload of a stale draft has to be a REMOUNT -
 * without one, the box would refetch and the stale draft would stay on screen,
 * which is the one thing that box exists to fix.
 *
 * The key is therefore the experience's id plus a COUNTER this screen owns. The
 * obvious spelling - keying on `draft.updatedAt` - is a defect: every autosave
 * answers with a new stamp and writes it into this query, so the key would
 * change roughly once a second while somebody types, remounting the field under
 * their caret and throwing the open tab back to the first one on every
 * keystroke. A counter cannot be moved by a data update, only by the gesture
 * that asks for a reload; and the bump waits for the refetch to LAND, because
 * remounting before the fresh answer arrives would re-seed the editor from the
 * very draft it is escaping.
 */

export const Route = createFileRoute('/admin/experiences/$experienceId')({
  validateSearch: readExperienceEditorSearch,
  component: ExperienceEditorPage,
});

function ExperienceEditorPage() {
  const { email } = useAdminSession();
  const { experienceId } = Route.useParams();
  const { version, tab, page, copie } = Route.useSearch();
  const navigate = useNavigate();

  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const experience = useQuery({
    queryKey: ['experience', email, experienceId],
    queryFn: () => fetchExperience(experienceId),
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['experience-version', email, experienceId, version ?? null],
    queryFn: () => fetchExperienceVersion(experienceId, version ?? ''),
    enabled: version !== undefined,
    retry: false,
  });

  /*
    The flag is CONSUMED once the editor it describes has OPENED (§4.2).

    Two halves, and both matter. Left in the address bar it would re-select
    the name field at every later remount - and the counter just above is
    one: « Recharger le brouillon du serveur » bumps it. Dropped too EARLY it
    would never select anything at all: the editor reads the flag on ITS
    mount, and it does not mount until the entity has been read - so this
    waits for the entity, and React runs a child's effects before its
    parent's, which is what puts the selection before the drop.

    `replace`, because it is a hand-off between two screens and not a place
    anybody goes back to.
  */
  useConsumedCopyFlag(copie, experience.data !== undefined, () =>
    navigate({
      to: '/admin/experiences/$experienceId',
      params: { experienceId },
      // Rebuilt from what THIS route holds - see the three other editors.
      search: {
        ...(version === undefined ? {} : { version }),
        ...(tab === undefined ? {} : { tab }),
        ...(page === undefined ? {} : { page }),
      },
      replace: true,
    }),
  );

  const rejected = useRejectOn401(experience.error);
  const missing = experience.error instanceof ApiError && experience.error.status === 404;

  /** Every navigation of this screen keeps `?version=` where it was. */
  const searchOf = (next: { tab?: string; page?: string }) => ({
    ...(version === undefined ? {} : { version }),
    ...next,
  });

  return (
    <div className="space-y-6">
      {experience.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement de l’expérience" />
        </div>
      ) : null}

      {missing ? (
        <p className="text-gray-700">
          Cette expérience n’existe pas.{' '}
          <Link to="/admin/experiences" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {experience.isError && !missing && !rejected ? (
        <p className="text-gray-700">Cette expérience n’a pas pu être chargée.</p>
      ) : null}

      {experience.data === undefined ? null : (
        <ExperienceEditor
          // The id and a counter, never the draft's stamp - see the head of this
          // file for what keying on the stamp would cost.
          key={`${experience.data.id}-${reload}`}
          selectName={copie === true}
          detail={experience.data}
          viewing={viewed.data ?? null}
          tab={tab}
          pageId={page ?? null}
          onSelectTab={(next) =>
            void navigate({
              to: '/admin/experiences/$experienceId',
              params: { experienceId },
              // The page is DROPPED when the tab changes: `?page=` names a row
              // of the Timeline, and carrying it onto the Persona tab would put
              // a parameter in the URL that nothing on screen answers to.
              search: searchOf({ tab: next }),
              replace: true,
            })
          }
          onOpenPlace={(place) =>
            void navigate({
              to: '/admin/experiences/$experienceId',
              params: { experienceId },
              search: searchOf(
                place.pageId === null ? { tab: place.tab } : { tab: place.tab, page: place.pageId },
              ),
              // The screen stays the same - only `?page=`/`?tab=` move - so the
              // router must not reset the scroll to the top (issue #90).
              resetScroll: false,
            })
          }
          onView={(summary) =>
            navigate({
              to: '/admin/experiences/$experienceId',
              params: { experienceId },
              search: {
                version: summary.id,
                ...(tab === undefined ? {} : { tab }),
                ...(page === undefined ? {} : { page }),
              },
            })
          }
          onCloseView={() =>
            navigate({
              to: '/admin/experiences/$experienceId',
              params: { experienceId },
              search: {
                ...(tab === undefined ? {} : { tab }),
                ...(page === undefined ? {} : { page }),
              },
            })
          }
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void experience.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
